import React from 'react';
import {
  BarChart3, Network, LayoutGrid, MoreHorizontal, Search, Swords, Building2, LineChart, LayoutDashboard, BookOpen, Table2, House, Map as MapIcon
} from 'lucide-react';

// Mobile navigation items (limited to 5 for bottom nav).
// Research was absent entirely — not in these five and not in the More menu —
// so on a phone the section that takes money had no route at all. It leads
// here for the same reason it leads the desktop rail. Orgs gives up the slot
// and moves to More: five is the ceiling, and it is the least direct of the
// four remaining paths to a deal.
// Redesign 2026-09-28: Home, Research and Data lead, as on the desktop rail;
// Deals keeps its slot as the Platform's front door, the rest of the Platform
// is under More.
const MOBILE_NAV_ITEMS = [
  { id: 'homepage', label: 'Home', icon: House },
  { id: 'research', label: 'Research', icon: BookOpen },
  { id: 'data', label: 'Data', icon: Table2 },
  { id: 'insights', label: 'Deals', icon: BarChart3 },
  { id: 'more', label: 'More', icon: MoreHorizontal, isMore: true },
];

export default function MobileBottomNav({
  activeTab,
  setActiveTab,
  badges = {},
  theme = 'light',
  onMoreClick,
}) {
  const isDark = theme === 'dark';

  return (
    <nav className={`
      fixed bottom-0 left-0 right-0 z-50 lg:hidden
      ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}
      border-t shadow-lg
    `}>
      <div className="flex items-center justify-around h-14 px-2">
        {MOBILE_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = !item.isMore && activeTab === item.id;
          const badge = item.id !== 'more' ? badges[item.id] : null;
          
          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.isMore) {
                  onMoreClick?.();
                } else {
                  setActiveTab(item.id);
                }
              }}
              className={`
                relative flex flex-col items-center justify-center py-1 px-3 rounded-lg transition-colors
                ${isActive 
                  ? `${isDark ? 'text-primary-400' : 'text-primary-700'}` 
                  : `${isDark ? 'text-slate-400' : 'text-slate-500'}`
                }
              `}
            >
              <div className="relative">
                <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                {badge > 0 && (
                  <span className={`
                    absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 
                    flex items-center justify-center
                    text-[10px] font-bold rounded-full
                    ${isDark ? 'bg-red-500 text-white' : 'bg-red-500 text-white'}
                  `}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
              <span className={`text-[10px] mt-0.5 ${isActive ? 'font-semibold' : ''}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Safe area padding for iOS */}
      <div className="h-safe-area-inset-bottom bg-inherit" />
    </nav>
  );
}

/**
 * More Menu Modal for additional navigation items
 */
export function MobileMoreMenu({
  isOpen,
  onClose,
  activeTab,
  setActiveTab,
  theme = 'light',
  isAdmin = false,
}) {
  const isDark = theme === 'dark';
  // Same gate as the desktop rail — the two must not disagree about what a
  // given visitor can see.
  // Ecosystem and Matchup are out of the nav here too — admins only, matching
  // the desktop rail. The two must not disagree about what is on offer.
  const moreItems = [
    { id: 'home', label: 'Dash', icon: LayoutDashboard },
    { id: 'dashboard', label: 'Search', icon: Search },
    { id: 'organizations', label: 'Orgs', icon: Building2 },
    { id: 'signals', label: 'Signals', icon: LineChart },
    { id: 'ecosystem', label: 'Ecosystem', icon: Network },
    { id: 'matchup', label: 'Matchup', icon: Swords },
    { id: 'billing', label: 'Billing', icon: '💳' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    // Sherpa Atlas: infrastructure assets by country.
    { id: 'atlas', label: 'Atlas', icon: MapIcon },
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 z-40 lg:hidden"
        onClick={onClose}
      />
      
      {/* Menu */}
      <div className={`
        fixed bottom-14 left-0 right-0 z-50 lg:hidden
        ${isDark ? 'bg-slate-900' : 'bg-white'}
        rounded-t-2xl shadow-xl
      `}>
        <div className="p-2">
          {/* Handle */}
          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-3" />
          
          {/* Menu Items */}
          <div className="grid grid-cols-4 gap-2 px-2 pb-2">
            {moreItems.map((item) => {
              const isActive = activeTab === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    onClose();
                  }}
                  className={`
                    flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-colors
                    ${isActive 
                      ? `${isDark ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-50 text-primary-700'}` 
                      : `${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`
                    }
                  `}
                >
                  {typeof item.icon === 'string' ? (
                    <span className="text-xl mb-1">{item.icon}</span>
                  ) : (
                    <item.icon size={22} className="mb-1" />
                  )}
                  <span className="text-xs font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
